import { TTopExperienceRequestParams } from "domain/entities/top-experience-response";
import { TrafficSourceSummaryModel } from "domain/models/traffic-source-summary-model";
import {
  IReportsRepository,
  TExportDropOffCSVParams,
  TReportsRequestParams,
  TReportsShowCardsRequestParams,
} from "../../domain/repositories/reports-repository";
import { IHttpClient } from "../protocols/http";
import { InsightsOnTagsModel } from "../../domain/models/insights-on-tags-model";
import { ViewportRankingModel } from "domain/models/view-port-ranking-model";
import { keysFromCapitalToCamelCase } from "../../utils/formatters/keysFromCapitalCaseToCamelCase";
import { PageSummaryModel } from "domain/models/page-summary-model";
import { CreativeSummaryModel } from "domain/models/creative-summary-model";
import { TPageSummary } from "domain/entities/page-summary-response";
import { TCreativeSummary } from "domain/entities/creative-summary-response";
import { TrafficSourceSummary } from "domain/entities/traffic-source-summary-response";
import { TInsightsOnTags } from "domain/entities/insights-on-tags";
import { TViewPortRanking } from "domain/entities/view-port-ranking-response";
import { TCampaignSummary } from "domain/entities/campaign-summary-response";
import { CampaignSummaryModel } from "domain/models/campaign-summary-model";
import { TTopSummaries } from "domain/entities/top-summaries-response";
import { TPageSpeed } from "domain/entities/page-speed-response";
import { PageSpeedModel } from "domain/models/page-speed-model";
import { TCreativeGoal } from "domain/entities/creative-goal-response";
import { CreativeGoalModel } from "domain/models/creative-goal-model";
import { TVisitorsFunnelResponse } from "domain/entities/visitors-funnel-response";

export class ReportsDatasource implements IReportsRepository {
  constructor(
    private httpClient: IHttpClient,
    private baseUrl: string = process.env.REACT_APP_API_URL || ""
  ) {}

  async getInsightsOnTraffic(
    params: TReportsShowCardsRequestParams
  ): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/InsightsOnTraffic",
        params,
      })
      .then(({ body }) => body.data);
  }

  async getCountryTrafficRanking(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/InsightsOnTraffic/geolocation",
        params,
      })
      .then(({ body }) => body.data);
  }

  async getPageSummary(
    params: TReportsShowCardsRequestParams
  ): Promise<TPageSummary> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/PageSummary",
        params,
      })
      .then(({ body }) => PageSummaryModel.from(body.data));
  }

  async getCreativeSummary(
    params: TReportsRequestParams
  ): Promise<TCreativeSummary> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/CreativeSummary",
        params,
      })
      .then(({ body }) => CreativeSummaryModel.from(body.data));
  }

  async getTrafficSourceSummary(
    params: TReportsShowCardsRequestParams
  ): Promise<TrafficSourceSummary> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/TrafficSourceSummary",
        params,
      })
      .then(({ body }) => TrafficSourceSummaryModel.from(body.data));
  }

  async getDayPartingRange(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/DayPartings",
        params,
      })
      .then(({ body }) => body.data);
  }

  async getInsightsOnTags(
    params: TReportsShowCardsRequestParams
  ): Promise<TInsightsOnTags> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/InsightsOnTags",
        params,
      })
      .then(({ body }) => InsightsOnTagsModel.from(body.data));
  }

  async getDropOffs(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/Dropoffs",
        params,
      })
      .then(({ body }) => body.data);
  }

  async exportTrafficSourceSummaryCSV(
    params: TReportsRequestParams
  ): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/TrafficSourceSummary/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async exportDropOffCSV(params: TExportDropOffCSVParams): Promise<any> {
    return this.httpClient
      .request({
        method: "POST",
        baseUrl: this.baseUrl,
        url: "/v1/Dropoffs/generate-csv",
        responseType: "blob",
        body: keysFromCapitalToCamelCase(params),
      })
      .then(({ body }) => body);
  }

  async exportInsightsOnTagsCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/InsightsOnTags/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async exportDayPartingCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/DayPartings/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async getHighlights(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/Highlights",
        params,
      })
      .then(({ body }) => body.data);
  }

  async exportInsightsOnTrafficCSV(
    params: TReportsRequestParams
  ): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/InsightsOnTraffic/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async exportHighlightsCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/Highlights/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async getTopExperience(params: TTopExperienceRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/Highlights/top-experience",
        params,
      })
      .then(({ body }) => body.data);
  }

  async getViewPortRanking(
    params: TReportsShowCardsRequestParams
  ): Promise<TViewPortRanking> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/ViewportRanking",
        params,
      })
      .then(({ body }) => ViewportRankingModel.from(body.data));
  }

  async exportPageSummaryCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/PageSummary/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async exportCreativeSummaryCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/CreativeSummary/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async getCampaignSummary(
    params: TReportsRequestParams
  ): Promise<TCampaignSummary> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/CampaignSummary",
        params,
      })
      .then(({ body }) => CampaignSummaryModel.from(body.data));
  }

  async getTopSummaries(params: TReportsRequestParams): Promise<TTopSummaries> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/TopSummaries",
        params,
      })
      .then(({ body }) => body.data);
  }

  async getPageSpeed(params: TReportsRequestParams): Promise<TPageSpeed> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/PageSpeed",
        params,
      })
      .then(({ body }) => PageSpeedModel.from(body.data));
  }

  async getCreativeGoal(params: TReportsRequestParams): Promise<TCreativeGoal> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/CreativeGoal",
        params,
      })
      .then(({ body }) => CreativeGoalModel.from(body.data));
  }

  async exportCampaignSummaryCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/CampaignSummary/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async exportPageSpeedCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/PageSpeed/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async exportTopSummariesCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/TopSummaries/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async exportViewportRankingCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/ViewportRanking/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async exportCreativeGoalCSV(params: TReportsRequestParams): Promise<any> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/CreativeGoal/export-csv",
        responseType: "blob",
        params,
      })
      .then(({ body }) => body);
  }

  async getVisitorsFunnel(
    params: TReportsRequestParams
  ): Promise<TVisitorsFunnelResponse> {
    return this.httpClient
      .request({
        method: "GET",
        baseUrl: this.baseUrl,
        url: "/v1/Highlights/visitors-funnel",
        params,
      })
      .then(({ body }) => body.data);
  }
}
